import { ElMessage } from 'element-plus'
import http from './http'

/**
 * 上传业务模块固定类型（阿里云 OSS）
 */
export const AliyunUploadModule = {
  /** 语音 */
  VOICE: 'voice'
} as const

export type AliyunUploadModuleType = (typeof AliyunUploadModule)[keyof typeof AliyunUploadModule]

interface AliyunPostPolicyData {
  /** 上传域名，例如：yt-audio-2026.oss-accelerate.aliyuncs.com */
  host: string
  /** 对象存储路径 */
  key: string
  /** 表单上传策略（Base64） */
  policy: string
  /** 临时凭证 AccessKeyId */
  accessKeyId: string
  /** 签名 */
  signature: string
  /** 过期时间（秒级时间戳） */
  expire: number
}

/**
 * 从 File 对象中提取扩展名
 */
const getFileExt = (file: File): string => {
  const fromName = file.name.split('.').pop()?.trim().toLowerCase()
  if (fromName && /^[a-z0-9]{1,10}$/.test(fromName)) {
    return fromName
  }
  if (file.type) {
    return file.type.split('/').pop() || ''
  }
  return ''
}

/**
 * 获取阿里云 OSS 直传配置（自动携带 token）
 * @param module 业务模块名称，使用 AliyunUploadModule 常量
 * @param ext 文件扩展名，例如：mp3、wav
 */
const getPostPolicy = (
  module: AliyunUploadModuleType,
  ext: string
): Promise<AliyunPostPolicyData> =>
  http
    // skipMsg：统一由 uploadToAliyunOss / 调用方提示错误，避免双重弹窗
    .get<AliyunPostPolicyData>('/api/aliyun/oss/v2/postPolicy', { module, ext }, { skipMsg: true })
    .then((res) => res.data)

/**
 * 解析 OSS 返回的 XML 错误信息
 */
const parseOssError = (xml: string): string => {
  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(xml, 'application/xml')
    const message = doc.querySelector('Message')?.textContent
    const code = doc.querySelector('Code')?.textContent
    return message || code || xml
  } catch {
    return xml
  }
}

/**
 * 上传文件到阿里云 OSS（PostObject 表单直传，无重试）
 * @param file 待上传文件
 * @param module 业务模块名称，使用 AliyunUploadModule 常量，默认 voice
 * @param ext 文件扩展名，可选，默认从 file.name 或 file.type 推导
 * @param skipMsg 是否跳过错误提示
 * @returns 上传后的访问 URL
 */
const uploadToAliyunOss = async (
  file: File,
  module: AliyunUploadModuleType = AliyunUploadModule.VOICE,
  ext?: string,
  skipMsg = false
): Promise<string> => {
  const fileExt = ext || getFileExt(file)
  if (!fileExt) {
    const err = '无法识别文件类型，请传入 ext 参数'
    !skipMsg && ElMessage({ message: '上传错误：' + err, type: 'error' })
    return Promise.reject(err)
  }

  let policy: AliyunPostPolicyData
  try {
    policy = await getPostPolicy(module, fileExt)
  } catch (err: any) {
    const msg = err?.msg || err?.message || '获取上传配置失败'
    !skipMsg && ElMessage({ message: '上传错误：' + msg, type: 'error' })
    return Promise.reject(msg)
  }

  // host 可能带协议头，统一处理
  const host = /^https?:\/\//.test(policy.host) ? policy.host : `https://${policy.host}`

  const formData = new FormData()
  formData.append('key', policy.key)
  formData.append('policy', policy.policy)
  formData.append('OSSAccessKeyId', policy.accessKeyId)
  formData.append('signature', policy.signature)
  formData.append('success_action_status', '200')
  // file 必须是最后一个字段
  formData.append('file', file)

  try {
    const res = await fetch(host, {
      method: 'POST',
      body: formData
    })
    if (!res.ok) {
      const text = await res.text()
      throw new Error(parseOssError(text) || `上传失败 (${res.status})`)
    }
    return `${host.replace(/\/$/, '')}/${policy.key}`
  } catch (err: any) {
    const msg = err?.message || '网络请求失败'
    !skipMsg && ElMessage({ message: '上传错误：' + msg, type: 'error' })
    return Promise.reject(msg)
  }
}

export default uploadToAliyunOss
